import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail, AlertCircle } from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setError('No verification token provided. Please check the link in your email.');
      setVerifying(false);
      return;
    }

    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    setVerifying(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/public/member-registration/verify?token=${encodeURIComponent(token)}`);
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 404) {
          setError('Invalid or expired verification link. Please request a new verification email.');
        } else if (response.status === 400 && data.detail?.includes('already verified')) {
          setError('This email address has already been verified. Your registration is pending admin approval.');
        } else {
          setError(data.detail || 'Verification failed. Please try again or contact the studio.');
        }
        return;
      }

      // Success
      setSuccess(true);
      setEmail(data.email || '');

    } catch (err) {
      console.error('Verification error:', err);
      setError('An error occurred during verification. Please try again or contact the studio at (352) 428-0086.');
    } finally {
      setVerifying(false);
    }
  };

  const handleResendRequest = () => {
    // Navigate to pending page where user can resend
    navigate('/register/verify-pending', { state: { email } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              {verifying && (
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
              )}
              {success && (
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              )}
              {error && !verifying && (
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
              )}
            </div>

            <CardTitle className="text-2xl">
              {verifying && 'Verifying Your Email...'}
              {success && 'Email Verified!'}
              {error && !verifying && 'Verification Failed'}
            </CardTitle>

            <CardDescription>
              {verifying && 'Please wait while we verify your email address'}
              {success && 'Your email has been successfully verified'}
              {error && !verifying && 'We couldn\'t verify your email address'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Success state */}
            {success && (
              <>
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Thank you for verifying your email! Your registration is now pending approval from our team.
                  </AlertDescription>
                </Alert>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">What happens next?</h3>

                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-blue-600 font-semibold text-sm">1</span>
                      </div>
                      <div>
                        <p className="font-medium">Admin review</p>
                        <p className="text-sm text-gray-600">
                          Our team will review your registration within 24-48 hours
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-blue-600 font-semibold text-sm">2</span>
                      </div>
                      <div>
                        <p className="font-medium">Approval notification</p>
                        <p className="text-sm text-gray-600">
                          You'll receive an email when your registration is approved
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium">Welcome email</p>
                        <p className="text-sm text-gray-600">
                          You'll receive login instructions for the member portal and can start your martial arts journey!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6 text-center">
                  <p className="text-sm text-gray-600 mb-4">
                    Questions about your registration?
                  </p>
                  <p className="text-sm text-gray-600">
                    Contact us at <a href="tel:+13524280086" className="text-red-600 hover:text-red-700 underline">(352) 428-0086</a> or{' '}
                    <a href="mailto:info@owazamartialarts.com" className="text-red-600 hover:text-red-700 underline">info@owazamartialarts.com</a>
                  </p>
                </div>
              </>
            )}

            {/* Error state */}
            {error && !verifying && (
              <>
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">What can you do?</h3>

                  <div className="space-y-2 text-sm text-gray-600">
                    <p>• Check that you clicked the complete verification link from your email</p>
                    <p>• Verification links expire after 24 hours - request a new one if needed</p>
                    <p>• If you've already verified, your registration may be pending admin approval</p>
                    <p>• Contact us if you continue to experience issues</p>
                  </div>

                  {email && (
                    <Button
                      onClick={handleResendRequest}
                      variant="outline"
                      className="w-full mt-4"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Request New Verification Email
                    </Button>
                  )}
                </div>

                <div className="border-t pt-6">
                  <p className="text-sm text-gray-600 mb-4 text-center">
                    Need assistance?
                  </p>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">
                      Call <a href="tel:+13524280086" className="text-red-600 hover:text-red-700 underline font-medium">(352) 428-0086</a>
                    </p>
                    <p className="text-sm text-gray-600">
                      Email <a href="mailto:info@owazamartialarts.com" className="text-red-600 hover:text-red-700 underline font-medium">info@owazamartialarts.com</a>
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Verifying state */}
            {verifying && (
              <div className="text-center py-8">
                <p className="text-gray-600">Please wait while we verify your email address...</p>
              </div>
            )}

            {/* Back to home */}
            <div className="text-center pt-4">
              <Link to="/" className="text-sm text-red-600 hover:text-red-700 underline">
                Return to Homepage
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
